import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import DateInput from '~/components/DateInput'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import Table from '~/components/Table'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { auditService } from '~/services/audit.service'
import { userService } from '~/services/user.service'
import type { AuditEventFiltersType, AuditEventType } from '~/types/Audit'
import type { ColumnType } from '~/types/Table'
import { resolveError } from '~/utils/error'
import { formatDateTime } from '~/utils/settings'
import AuditEventModal from './AuditEventModal'
import { ACTION_OPTIONS, actionStyle, SEVERITY_OPTIONS, severityStyle } from './audit-labels'

/** Tarjeta de una métrica del encabezado. */
const Metrica: Component<{ label: string; value: string | number; icon: string; hint?: string }> = props => (
  <div class="rounded-xl border border-border bg-surface p-4">
    <div class="flex items-center gap-2 text-foreground-subtle">
      <Icon icon={props.icon} size={18} />
      <p class="text-xs">{props.label}</p>
    </div>
    <p class="mt-1 text-2xl font-bold text-foreground">{props.value}</p>
    <Show when={props.hint}>
      <p class="text-xs text-foreground-subtle">{props.hint}</p>
    </Show>
  </div>
)

/**
 * Listado de los movimientos registrados por la auditoría: del más reciente al
 * más antiguo, con filtros, métricas del mismo conjunto filtrado y el detalle
 * completo de cada evento en un modal.
 */
const TabRegistros: Component = () => {
  const [busqueda, busquedaDebounced, setBusqueda] = useDebouncedSignal('')
  const [accion, setAccion] = createSignal('')
  const [severidad, setSeveridad] = createSignal('')
  const [recurso, setRecurso] = createSignal('')
  const [usuario, setUsuario] = createSignal('')
  const [desde, setDesde] = createSignal('')
  const [hasta, setHasta] = createSignal('')
  const [filtrosAbiertos, setFiltrosAbiertos] = createSignal(false)
  const [detalle, setDetalle] = createSignal<AuditEventType | null>(null)

  /** Los mismos filtros para la lista y para las métricas. */
  const filtros = (): AuditEventFiltersType => {
    const params: AuditEventFiltersType = {}
    const texto = busquedaDebounced().trim()
    if (texto) params.search = texto
    if (accion()) params.event_action = accion()
    if (severidad()) params.severity = severidad()
    if (recurso()) params.resource_type = recurso()
    if (usuario()) params.user_id = usuario()
    // Las fechas llegan como `YYYY-MM-DD`: el día completo va de las 00:00 a
    // las 23:59, si no un filtro "hasta hoy" dejaría fuera lo de hoy.
    if (desde()) params.from = `${desde()} 00:00:00`
    if (hasta()) params.to = `${hasta()} 23:59:59`
    return params
  }

  const table = useServerTable<AuditEventType>({
    fetcher: params => auditService.listEvents(params as Parameters<typeof auditService.listEvents>[0]),
    params: () => filtros() as Record<string, string | number | undefined>
  })

  /**
   * Las métricas no pueden tumbar la pantalla: si fallan, las tarjetas quedan
   * en blanco y la lista (que trae su propio error) sigue funcionando.
   */
  const [stats] = createResource(filtros, async params => {
    try {
      return await auditService.getStats(params)
    } catch {
      return null
    }
  })

  /**
   * Los eventos guardan el id del usuario, no su nombre. Se resuelven una vez
   * contra el listado de usuarios de la empresa: mostrar "#7" en cada fila
   * obligaría a salir de la pantalla para saber quién fue.
   */
  const [usuarios] = createResource(async () => {
    try {
      const { items } = await userService.list({ limit: 200 })
      return items
    } catch {
      return []
    }
  })

  const nombreUsuario = (userId: number | null): string => {
    if (!userId) return 'Sistema'
    const encontrado = usuarios()?.find(u => u.id === userId)
    return encontrado?.name ?? `#${userId}`
  }

  /** Los recursos que existen de verdad, salidos de las métricas. */
  const opcionesRecurso = createMemo(() => (stats()?.byResourceType ?? []).map(f => ({ value: f.value, title: `${f.value} (${f.total})` })))

  const opcionesUsuario = createMemo(() =>
    (stats()?.byUser ?? []).map(f => ({
      value: f.value,
      title: `${nombreUsuario(Number(f.value))} (${f.total})`
    }))
  )

  const filtrosActivos = createMemo(() => [accion(), severidad(), recurso(), usuario(), desde(), hasta()].filter(Boolean).length)

  const limpiarFiltros = () => {
    setAccion('')
    setSeveridad('')
    setRecurso('')
    setUsuario('')
    setDesde('')
    setHasta('')
  }

  const chips = createMemo(() => {
    const lista: { label: string; onRemove: () => void }[] = []
    if (accion()) lista.push({ label: `Acción: ${actionStyle(accion()).label}`, onRemove: () => setAccion('') })
    if (severidad()) lista.push({ label: `Severidad: ${severityStyle(severidad()).label}`, onRemove: () => setSeveridad('') })
    if (recurso()) lista.push({ label: `Recurso: ${recurso()}`, onRemove: () => setRecurso('') })
    if (usuario()) lista.push({ label: `Usuario: ${nombreUsuario(Number(usuario()))}`, onRemove: () => setUsuario('') })
    if (desde()) lista.push({ label: `Desde: ${desde()}`, onRemove: () => setDesde('') })
    if (hasta()) lista.push({ label: `Hasta: ${hasta()}`, onRemove: () => setHasta('') })
    return lista
  })

  const columns: ColumnType<AuditEventType>[] = [
    {
      header: 'Fecha y hora',
      accessor: (evento: AuditEventType) => <span class="whitespace-nowrap text-sm">{formatDateTime(evento.event_timestamp)}</span>,
      minWidth: 170
    },
    {
      header: 'Acción',
      accessor: (evento: AuditEventType) => (
        <Chip size="xs" variant="soft" color={actionStyle(evento.event_action).color} icon={actionStyle(evento.event_action).icon} class="shrink-0 whitespace-nowrap">
          {actionStyle(evento.event_action).label}
        </Chip>
      ),
      minWidth: 150
    },
    {
      header: 'Evento',
      accessor: (evento: AuditEventType) => <span class="font-medium text-foreground">{evento.event_type}</span>,
      minWidth: 190
    },
    {
      header: 'Recurso',
      accessor: (evento: AuditEventType) => (
        <span class="text-sm">
          {evento.resource_type || '—'}
          <Show when={evento.resource_id}>
            <span class="text-foreground-subtle"> #{evento.resource_id}</span>
          </Show>
        </span>
      ),
      minWidth: 160
    },
    {
      header: 'Usuario',
      accessor: (evento: AuditEventType) => <span class="text-sm">{nombreUsuario(evento.user_id)}</span>,
      minWidth: 150
    },
    {
      header: 'Severidad',
      accessor: (evento: AuditEventType) => (
        <Chip size="xs" variant="soft" color={severityStyle(evento.severity).color} class="shrink-0 whitespace-nowrap">
          {severityStyle(evento.severity).label}
        </Chip>
      ),
      minWidth: 130
    },
    {
      header: 'Petición',
      // `route` ya viene como "GET /api/...": añadir el método lo duplicaba.
      accessor: (evento: AuditEventType) => <span class="font-mono text-xs text-foreground-subtle">{evento.route || '—'}</span>,
      minWidth: 220
    }
  ]

  return (
    <>
      <Show when={table.error()}>
        <Alert variant="danger" class="mb-4">
          {resolveError(table.error())}
        </Alert>
      </Show>

      {/* Métricas del conjunto filtrado */}
      <div class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metrica label="Eventos" value={stats()?.total ?? 0} icon="material-symbols-light:database-outline" hint={stats()?.source === 'postgres' ? 'Leídos del outbox' : 'Leídos de ClickHouse'} />
        <Metrica label="Último evento" value={stats()?.lastEventAt ? formatDateTime(stats()?.lastEventAt) : '—'} icon="material-symbols-light:schedule-outline" />
        <Metrica
          label="Acción más frecuente"
          value={stats()?.byAction?.[0] ? actionStyle(stats()!.byAction[0].value).label : '—'}
          icon="material-symbols-light:bolt-outline"
          hint={stats()?.byAction?.[0] ? `${stats()!.byAction[0].total} eventos` : undefined}
        />
        <Metrica
          label="Recurso más tocado"
          value={stats()?.byResourceType?.[0]?.value ?? '—'}
          icon="material-symbols-light:inventory-2-outline"
          hint={stats()?.byResourceType?.[0] ? `${stats()!.byResourceType[0].total} eventos` : undefined}
        />
      </div>

      {/* Reparto por acción y severidad: el pulso del sistema de un vistazo */}
      <Show when={(stats()?.byAction?.length ?? 0) > 0 || (stats()?.bySeverity?.length ?? 0) > 0}>
        <div class="mb-4 flex flex-wrap items-center gap-2">
          <span class="text-xs text-foreground-subtle">Acciones:</span>
          <For each={stats()?.byAction ?? []}>
            {faceta => (
              <Chip
                size="sm"
                variant={accion() === faceta.value ? 'filled' : 'soft'}
                color={actionStyle(faceta.value).color}
                icon={actionStyle(faceta.value).icon}
                class="shrink-0 cursor-pointer whitespace-nowrap"
                onClick={() => setAccion(accion() === faceta.value ? '' : faceta.value)}
              >
                {actionStyle(faceta.value).label}: {faceta.total}
              </Chip>
            )}
          </For>
          <Show when={(stats()?.bySeverity ?? []).some(f => f.value !== 'info')}>
            <span class="ms-2 text-xs text-foreground-subtle">Severidad:</span>
          </Show>
          <For each={(stats()?.bySeverity ?? []).filter(f => f.value !== 'info')}>
            {faceta => (
              <Chip
                size="sm"
                variant={severidad() === faceta.value ? 'filled' : 'soft'}
                color={severityStyle(faceta.value).color}
                class="shrink-0 cursor-pointer whitespace-nowrap"
                onClick={() => setSeveridad(severidad() === faceta.value ? '' : faceta.value)}
              >
                {severityStyle(faceta.value).label}: {faceta.total}
              </Chip>
            )}
          </For>
        </div>
      </Show>

      <Table<AuditEventType>
        title="Movimientos registrados"
        subtitle="Del más reciente al más antiguo. Pulsa una fila para ver el detalle."
        headerActions={
          <div class="flex items-center gap-2">
            <FiltersButton onClick={() => setFiltrosAbiertos(true)} activeCount={filtrosActivos()} />
            <Button variant="soft" color="default" onClick={() => table.refetch()} prependIcon="material-symbols-light:refresh-outline" tooltip="Recargar">
              Recargar
            </Button>
          </div>
        }
        data={table.data()}
        columns={columns}
        keyField="event_id"
        loading={table.loading()}
        searchable
        searchPlaceholder="Buscar por evento, recurso o ruta..."
        searchValue={busqueda()}
        onSearch={setBusqueda}
        serverSide
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        filterChips={chips()}
        onRowClick={evento => setDetalle(evento)}
        headSticky
        emptyMessage="No hay eventos que coincidan con estos filtros"
      />

      <TableFilters title="Filtros de auditoría" isOpen={filtrosAbiertos()} onClose={() => setFiltrosAbiertos(false)} activeCount={filtrosActivos()} onClear={limpiarFiltros}>
        <Select label="Acción" options={ACTION_OPTIONS} value={accion()} clearable onChange={value => setAccion(String(value ?? ''))} placeholder="Todas" />
        <Select label="Severidad" options={SEVERITY_OPTIONS} value={severidad()} clearable onChange={value => setSeveridad(String(value ?? ''))} placeholder="Todas" />
        <Select label="Recurso" options={opcionesRecurso()} value={recurso()} clearable onChange={value => setRecurso(String(value ?? ''))} placeholder="Todos" />
        <Select label="Usuario" options={opcionesUsuario()} value={usuario()} clearable onChange={value => setUsuario(String(value ?? ''))} placeholder="Todos" />
        <DateInput mode="date" label="Desde" value={desde()} onChange={setDesde} />
        <DateInput mode="date" label="Hasta" value={hasta()} onChange={setHasta} />
      </TableFilters>

      <AuditEventModal event={detalle()} onClose={() => setDetalle(null)} userName={nombreUsuario} />
    </>
  )
}

export default TabRegistros
